import type { ChecklistSnapshotCanonicalReconciliationInput } from '@/adapters/IDatabaseAdapter';
import {
  buildTemplateSchemaPayload,
  CHECKLIST_SNAPSHOTS_FEATURE,
  stableStringify,
} from './ChecklistSnapshotCreationService';
import type { TemplateField } from './ChecklistSnapshotCreationService';
import { realtimeTrigger } from '@/state/realtime';

export interface ChecklistSnapshotRemoteCreateDatabase {
  listItems<T>(collection: string, query?: Record<string, unknown>): Promise<T[]>;
  bulkUpsert<T>(collection: string, rows: T[], skipPendingOperation?: boolean): Promise<void>;
  reconcileChecklistSnapshotCanonical?(
    input: ChecklistSnapshotCanonicalReconciliationInput
  ): Promise<void>;
}

export interface ChecklistSnapshotRemoteCreateTables {
  checklists: string;
  templateSchemaVersions: string;
  snapshots: string;
  snapshotFields: string;
}

export interface ChecklistSnapshotRemoteCreateDependencies {
  database: ChecklistSnapshotRemoteCreateDatabase;
  tables: ChecklistSnapshotRemoteCreateTables;
  getClientBuild(): number;
  isFeatureEnabled(input: {
    companyId: string;
    featureKey: string;
    clientBuild: number;
  }): Promise<boolean>;
  callRpc(input: {
    checklist: Record<string, unknown>;
    snapshotId: string;
    fields: Array<Record<string, unknown>>;
    clientBuild: number;
  }): Promise<unknown>;
  normalizeChecklist(checklist: Record<string, unknown>): Record<string, unknown>;
}

type SnapshotCreateRpcPayload = {
  checklist: Record<string, unknown>;
  template_schema_version: Record<string, unknown>;
  snapshot: Record<string, unknown>;
  fields: Array<Record<string, unknown>>;
};

const parseConfig = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== 'string' || !value.trim()) return {};
  const parsed = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Config local de campo do snapshot invalida.');
  }
  return parsed as Record<string, unknown>;
};

const timestamp = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Date.parse(String(value));
  if (!Number.isFinite(parsed)) throw new Error('Timestamp remoto de snapshot invalido.');
  return parsed;
};

const parseObject = (value: unknown, message: string): Record<string, unknown> => {
  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      throw new Error(message);
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(message);
  }
  return parsed as Record<string, unknown>;
};

const normalizeJsonRecord = (
  row: Record<string, unknown>,
  message: string
): Record<string, unknown> => ({
  ...row,
  schema_payload: stableStringify(parseObject(row.schema_payload, message)),
  created_at: timestamp(row.created_at),
});

const asRequiredString = (value: unknown, message: string): string => {
  if (typeof value !== 'string' || value.trim().length === 0) throw new Error(message);
  return value;
};

const asBoolean = (value: unknown, fallback: boolean): boolean => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== '0' && normalized !== 'false';
};

export const normalizeChecklistSnapshotHistoricalRow = (
  collection: string,
  row: Record<string, unknown>
): Record<string, unknown> => {
  if (
    collection === 'guincho_template_schema_versions'
    || collection === 'guincho_checklist_snapshots'
  ) {
    return normalizeJsonRecord(row, 'Payload remoto de snapshot invalido.');
  }
  if (collection === 'guincho_checklist_snapshot_fields') {
    return {
      ...row,
      config: stableStringify(parseConfig(row.config)),
      obrigatorio: asBoolean(row.obrigatorio, false) ? 1 : 0,
      visivel: asBoolean(row.visivel, true) ? 1 : 0,
      created_at: timestamp(row.created_at),
    };
  }
  return row;
};

const normalizeComparableField = (field: Record<string, unknown>): Record<string, unknown> => ({
  id: asRequiredString(field.id, 'Campo remoto do snapshot sem id.'),
  original_campo_id: asRequiredString(
    field.original_campo_id,
    'Campo remoto do snapshot sem original_campo_id.'
  ),
  field_key: asRequiredString(field.field_key, 'Campo remoto do snapshot sem field_key.'),
  grupo: field.grupo ?? null,
  nome: asRequiredString(field.nome, 'Campo remoto do snapshot sem nome.'),
  tipo: asRequiredString(field.tipo, 'Campo remoto do snapshot sem tipo.'),
  ordem: field.ordem === null || field.ordem === undefined ? null : Number(field.ordem),
  obrigatorio: asBoolean(field.obrigatorio, false),
  visivel: asBoolean(field.visivel, true),
  config: parseConfig(field.config),
});

const fieldSort = (left: Record<string, unknown>, right: Record<string, unknown>): number => (
  (Number(left.ordem ?? Number.MAX_SAFE_INTEGER) - Number(right.ordem ?? Number.MAX_SAFE_INTEGER))
  || String(left.field_key).localeCompare(String(right.field_key))
  || String(left.original_campo_id).localeCompare(String(right.original_campo_id))
);

const fieldMatches = (
  left: Record<string, unknown>,
  right: Record<string, unknown>
): boolean => stableStringify(left) === stableStringify(right);

const normalizeRpcFields = (
  localFields: Array<Record<string, unknown>>
): Array<Record<string, unknown>> => localFields
  .map((field, index) => {
    const order = field.ordem === null || field.ordem === undefined
      ? index
      : Number(field.ordem);
    if (!Number.isInteger(order)) throw new Error('Ordem local de campo do snapshot invalida.');
    return {
      id: asRequiredString(field.id, 'Campo local do snapshot sem id.'),
      original_campo_id: asRequiredString(
        field.original_campo_id,
        'Campo local do snapshot sem original_campo_id.'
      ),
      field_key: asRequiredString(field.field_key, 'Campo local do snapshot sem field_key.'),
      grupo: field.grupo ?? null,
      nome: asRequiredString(field.nome, 'Campo local do snapshot sem nome.'),
      tipo: asRequiredString(field.tipo, 'Campo local do snapshot sem tipo.'),
      ordem: order,
      obrigatorio: asBoolean(field.obrigatorio, false),
      visivel: asBoolean(field.visivel, true),
      config: parseConfig(field.config),
    };
  })
  .sort(fieldSort);

export class ChecklistSnapshotRemoteCreateService {
  constructor(private readonly dependencies: ChecklistSnapshotRemoteCreateDependencies) {}

  async process(input: {
    itemId: string;
    checklist: Record<string, unknown>;
  }): Promise<boolean | null> {
    const companyId = typeof input.checklist.empresa_id === 'string'
      ? input.checklist.empresa_id.trim()
      : '';
    if (!companyId) throw new Error('Empresa ausente no checklist com snapshot.');

    const clientBuild = this.dependencies.getClientBuild();
    const enabled = await this.dependencies.isFeatureEnabled({
      companyId,
      featureKey: CHECKLIST_SNAPSHOTS_FEATURE,
      clientBuild,
    });
    if (!enabled) {
      if (typeof input.checklist.snapshot_id === 'string' && input.checklist.snapshot_id.trim()) {
        throw new Error(
          'Feature de snapshot indisponivel para checklist estrutural; criacao remota mantida pendente.'
        );
      }
      return null;
    }

    const snapshots = await this.dependencies.database.listItems<Record<string, unknown>>(
      this.dependencies.tables.snapshots,
      { empresa_id: companyId, checklist_id: input.itemId }
    );
    if (snapshots.length !== 1 || typeof snapshots[0].id !== 'string') {
      throw new Error('Snapshot local unico ausente; criacao remota mantida pendente.');
    }
    const snapshot = snapshots[0];
    const snapshotId = snapshot.id as string;
    if (input.checklist.snapshot_id !== snapshotId) {
      throw new Error('Checklist local nao esta vinculado ao snapshot esperado.');
    }

    const templateId = asRequiredString(
      input.checklist.template_id,
      'Template ausente no checklist com snapshot.'
    );
    const templateVersion = Number(input.checklist.versao ?? 1);
    if (!Number.isInteger(templateVersion) || templateVersion <= 0) {
      throw new Error('Versao local do template invalida no checklist com snapshot.');
    }

    const localFields = await this.dependencies.database.listItems<Record<string, unknown>>(
      this.dependencies.tables.snapshotFields,
      { empresa_id: companyId, snapshot_id: snapshotId }
    );
    if (localFields.length === 0) {
      throw new Error('Campos locais do snapshot ausentes; criacao remota mantida pendente.');
    }

    const rpcFields = normalizeRpcFields(localFields);
    const schemaFields = rpcFields.map(field => ({
      id: field.original_campo_id,
      empresa_id: companyId,
      template_id: templateId,
      nome: field.nome,
      tipo: field.tipo,
      grupo: field.grupo,
      ordem: field.ordem,
      obrigatorio: field.obrigatorio,
      visivel: field.visivel,
      ...parseConfig(field.config),
    })) as TemplateField[];
    const expectedSchemaPayload = buildTemplateSchemaPayload(
      templateId,
      templateVersion,
      schemaFields
    );
    const normalizedChecklist = this.dependencies.normalizeChecklist(input.checklist);
    const result = await this.dependencies.callRpc({
      checklist: normalizedChecklist,
      snapshotId,
      fields: rpcFields,
      clientBuild,
    }) as SnapshotCreateRpcPayload | null;

    const responseChecklist = result?.checklist;
    const responseSchema = result?.template_schema_version;
    const responseSnapshot = result?.snapshot;
    const responseFields = result?.fields;
    const canonicalSchemaPayload = responseSchema
      ? parseObject(responseSchema.schema_payload, 'Payload canonico de schema invalido.')
      : null;
    const canonicalSnapshotPayload = responseSnapshot
      ? parseObject(responseSnapshot.schema_payload, 'Payload canonico de snapshot invalido.')
      : null;
    const canonicalSchemaId = responseSchema?.id;
    const canonicalSchemaVersion = Number(responseSchema?.version);
    const responseSchemaHash = responseSchema?.schema_hash;
    const responseSnapshotHash = responseSnapshot?.schema_hash;

    if (
      !result
      || typeof result !== 'object'
      || !responseChecklist
      || responseChecklist.id !== input.itemId
      || responseChecklist.empresa_id !== companyId
      || responseChecklist.snapshot_id !== snapshotId
      || responseChecklist.template_id !== templateId
      || !responseSchema
      || typeof canonicalSchemaId !== 'string'
      || responseSchema.empresa_id !== companyId
      || responseSchema.template_id !== templateId
      || !Number.isInteger(canonicalSchemaVersion)
      || canonicalSchemaVersion <= 0
      || typeof responseSchemaHash !== 'string'
      || responseSchemaHash.trim().length === 0
      || stableStringify(canonicalSchemaPayload) !== stableStringify(expectedSchemaPayload)
      || !responseSnapshot
      || responseSnapshot.id !== snapshotId
      || responseSnapshot.checklist_id !== input.itemId
      || responseSnapshot.empresa_id !== companyId
      || responseSnapshot.template_id !== templateId
      || responseSnapshot.template_schema_version_id !== canonicalSchemaId
      || responseSnapshotHash !== responseSchemaHash
      || stableStringify(canonicalSnapshotPayload) !== stableStringify(expectedSchemaPayload)
      || !Array.isArray(responseFields)
      || responseFields.length !== rpcFields.length
      || responseFields.some(field => field.snapshot_id !== snapshotId || field.empresa_id !== companyId)
    ) {
      throw new Error('Resposta divergente ao criar checklist remoto com snapshot.');
    }

    const canonicalFields = responseFields.map(field => normalizeChecklistSnapshotHistoricalRow(
      this.dependencies.tables.snapshotFields,
      field
    ));
    const comparableCanonicalFields = canonicalFields
      .map(normalizeComparableField)
      .sort(fieldSort);
    const comparableRpcFields = rpcFields
      .map(normalizeComparableField)
      .sort(fieldSort);
    if (
      comparableCanonicalFields.length !== comparableRpcFields.length
      || comparableCanonicalFields.some((field, index) => !fieldMatches(field, comparableRpcFields[index]))
    ) {
      throw new Error('Resposta divergente ao criar checklist remoto com snapshot.');
    }

    const schemaVersion = normalizeChecklistSnapshotHistoricalRow(
      this.dependencies.tables.templateSchemaVersions,
      responseSchema
    );
    const canonicalSnapshot = normalizeChecklistSnapshotHistoricalRow(
      this.dependencies.tables.snapshots,
      responseSnapshot
    );
    const provisionalSchemaVersionId = typeof snapshot.template_schema_version_id === 'string'
      ? snapshot.template_schema_version_id
      : null;
    const reconciliationInput = {
      provisionalSchemaVersionId,
      schemaVersion: {
        collection: this.dependencies.tables.templateSchemaVersions,
        record: schemaVersion,
      },
      expectedChecklist: input.checklist,
      checklist: {
        collection: this.dependencies.tables.checklists,
        record: responseChecklist,
      },
      snapshot: {
        collection: this.dependencies.tables.snapshots,
        record: canonicalSnapshot,
      },
      snapshotFields: {
        collection: this.dependencies.tables.snapshotFields,
        records: canonicalFields,
      },
    } satisfies ChecklistSnapshotCanonicalReconciliationInput;

    if (this.dependencies.database.reconcileChecklistSnapshotCanonical) {
      await this.dependencies.database.reconcileChecklistSnapshotCanonical(reconciliationInput);
    } else {
      // Compatibilidade apenas para doubles antigos; o adapter de producao usa a transacao acima.
      await this.dependencies.database.bulkUpsert(
        this.dependencies.tables.templateSchemaVersions,
        [schemaVersion],
        true
      );
      await this.dependencies.database.bulkUpsert(
        this.dependencies.tables.checklists,
        [responseChecklist],
        true
      );
      await this.dependencies.database.bulkUpsert(
        this.dependencies.tables.snapshots,
        [canonicalSnapshot],
        true
      );
      await this.dependencies.database.bulkUpsert(
        this.dependencies.tables.snapshotFields,
        canonicalFields,
        true
      );
    }
    realtimeTrigger.counter.set(counter => counter + 1);
    return true;
  }
}
